/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext, useState, useEffect, useRef } from 'react';
import Logo from '../../images/Mask Group 1.png';
import ar from '../../images/ar.png';
import en from '../../images/en.png';
import config from '../../config/env';

import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageContext from '../../context/LanguageContext';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { Link } from "react-router-dom";
import { FaBars, FaXmark } from "react-icons/fa6";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartShopping } from '@fortawesome/free-solid-svg-icons';
import './header.css';

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
}

function Header() {
  const { t } = useTranslation();
  const { language, changeLanguage } = useContext(LanguageContext);
  const { cartCount } = useCart();
  const { user, isAuthenticated, isLoading, login, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const profileRef = useRef(null);
  const exploreRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
      if (exploreRef.current && !exploreRef.current.contains(e.target)) {
        setExploreOpen(false);
      }
    };
    if (profileOpen || exploreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen, exploreOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleRegisterClick = () => {
    navigate('/register');
  };

  const handleLoginClick = () => {
    login(location.pathname + location.search);
  };

  const handleLogoutClick = () => {
    setProfileOpen(false);
    logout();
  };

  const handleLanguageSelection = (courseLanguage) => {
    sessionStorage.setItem('courseLanguage', courseLanguage);
  };

  return (
    <header className={`p-3 ${scrolled ? 'scrolled' : ''} ${isMenuOpen ? 'menu-open' : ''}`}>
      <div className="container">
        <div className="row align-items-center justify-content-between">
          <div className="col-lg-3 col-6 responsive-header">
            <Link to="/" className="d-flex align-items-center text-decoration-none logo-header" aria-label="Go to homepage">
              <img className="logo-img arimg" src={Logo} alt="Learnovative Logo" />
            </Link>
            <div className="d-flex align-items-center gap-2">
              <Link to="/cart" className="header-cart-btn d-lg-none" aria-label={t('Shopping Cart')}>
                <FontAwesomeIcon icon={faCartShopping} />
                {cartCount > 0 && (
                  <span className="header-cart-badge">{cartCount}</span>
                )}
              </Link>

              <div className="language-switcher d-lg-none">
                {language === "en" ? (
                  <button
                    className="btn-lang-pill"
                    onClick={() => changeLanguage("ar")}
                    aria-label="Switch to Arabic"
                  >
                    <img src={ar} alt="Arabic" className="lang-flag-sm" />
                    <span className="lang-text">AR</span>
                  </button>
                ) : (
                  <button
                    className="btn-lang-pill"
                    onClick={() => changeLanguage("en")}
                    aria-label="Switch to English"
                  >
                    <img src={en} alt="English" className="lang-flag-sm" />
                    <span className="lang-text">EN</span>
                  </button>
                )}
              </div>

              {isAuthenticated && !isLoading && (
                <Link to="/profile" className="header-avatar-btn d-lg-none" aria-label={t('My Profile')}>
                  {getInitials(user?.name)}
                </Link>
              )}

              <button 
                className={`navbar-toggler ${isMenuOpen ? 'open' : ''}`} 
                type="button" 
                onClick={toggleMenu} 
                aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
                aria-expanded={isMenuOpen}
              >
                {isMenuOpen ? <FaXmark /> : <FaBars />}
              </button>
            </div>
          </div>

          <div className="col-lg-9 col-6 header-nav-wrapper">
            <nav className={`nav-menu ${isMenuOpen ? 'open' : ''}`} aria-label="Main navigation">
              <ul className="nav col-12 col-lg-auto me-lg-auto mb-2 justify-content-center mb-md-0">
                <li><Link to="/about" className="nav-link px-3 text-color">{t('About')}</Link></li>
                <li className={`nav-item dropdown ${exploreOpen ? 'show' : ''}`} ref={exploreRef}>
                  <button
                    type="button"
                    className="nav-link text-color dropdown-toggle"
                    onClick={() => setExploreOpen(!exploreOpen)}
                    aria-expanded={exploreOpen}
                    aria-haspopup="true"
                  >
                    {t('Explore')}
                  </button>
                  <ul className={`dropdown-menu ${exploreOpen ? 'show' : ''}`}>
                    <li><Link to="/courses" className="dropdown-item" onClick={() => setExploreOpen(false)}>{t('All')}</Link></li>
                    <li><Link to="/courses/english" className="dropdown-item" onClick={() => setExploreOpen(false)}>{t('EnglishCourses')}</Link></li>
                    <li><Link to="/courses/german" className="dropdown-item" onClick={() => { handleLanguageSelection(2); setExploreOpen(false); }}>{t('GermanCourses')}</Link></li>
                  </ul>
                </li>
                <li><Link to="/instructors" className="nav-link px-3 text-color">{t('Instructors')}</Link></li>
                <li><Link to="/contact" className="nav-link px-3 text-color">{t('Contact Us')}</Link></li>
                {isAuthenticated && (
                  <li className="d-none d-lg-block">
                    <a href={config.STUDENT_PORTAL_MY_COURSES_URL} target="_blank" rel="noopener noreferrer" className="nav-link px-3 text-color">
                      {t('My Courses')}
                    </a>
                  </li>
                )}
              </ul>

              <div className="d-flex align-items-center gap-2">
                <div className="language-switcher d-none d-lg-block">
                  {language === "en" ? (
                    <button
                      className="btn-lang-pill"
                      onClick={() => changeLanguage("ar")}
                      aria-label="Switch to Arabic"
                    >
                      <img src={ar} alt="Arabic" className="lang-flag-sm" />
                      <span className="lang-text">AR</span>
                    </button>
                  ) : (
                    <button
                      className="btn-lang-pill"
                      onClick={() => changeLanguage("en")}
                      aria-label="Switch to English"
                    >
                      <img src={en} alt="English" className="lang-flag-sm" />
                      <span className="lang-text">EN</span>
                    </button>
                  )}
                </div>

                <Link to="/cart" className="header-cart-btn" aria-label={t('Shopping Cart')}>
                  <FontAwesomeIcon icon={faCartShopping} />
                  {cartCount > 0 && (
                    <span className="header-cart-badge">{cartCount}</span>
                  )}
                </Link>

                <div className="auth-buttons d-flex gap-2">
                  {isLoading ? (
                    <div className="spinner-border spinner-border-sm text-light" role="status" style={{ width: '1.2rem', height: '1.2rem', alignSelf: 'center' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : isAuthenticated ? (
                    <div className="profile-dropdown-wrapper d-none d-lg-flex" ref={profileRef}>
                      <button
                        type="button"
                        className={`profile-trigger ${profileOpen ? 'active' : ''}`}
                        onClick={() => setProfileOpen(!profileOpen)}
                        aria-expanded={profileOpen}
                        aria-haspopup="true"
                      >
                        <span className="profile-trigger-avatar">{getInitials(user?.name)}</span>
                        <span className="profile-trigger-name">{user?.name}</span>
                        <svg className="profile-trigger-arrow" width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
                          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      {profileOpen && (
                        <div className="profile-dropdown-menu" role="menu">
                          <Link to="/profile" className="profile-dropdown-item" onClick={() => setProfileOpen(false)} role="menuitem">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                            </svg>
                            {t('My Profile')}
                          </Link>
                          <a href={config.STUDENT_PORTAL_MY_COURSES_URL} target="_blank" rel="noopener noreferrer" className="profile-dropdown-item" onClick={() => setProfileOpen(false)} role="menuitem">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                            </svg>
                            {t('My Courses')}
                          </a>
                          <hr className="profile-dropdown-divider" />
                          <button type="button" className="profile-dropdown-item logout" onClick={handleLogoutClick} role="menuitem">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
                            </svg>
                            {t('Logout')}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <button onClick={handleRegisterClick} type="button" className="btn-Register">{t('Register')}</button>
                      <button onClick={handleLoginClick} type="button" className="btn-Login">{t('Login')}</button>
                    </>
                  )}
                </div>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
